import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Building, MapPin, Globe, Briefcase, Check, Loader2, Phone, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { updateAdminCompany, uploadCompanyLogo, deleteCompanyLogo } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import { INDUSTRY_TYPE_LABELS } from '@/lib/constants';
import type { IndustryType, LogoDisplayMode } from '@/types';
import { ImageUploader } from '@/components/upload/ImageUploader';
import { IconInput, FormField } from '@/components/form';
import { HeaderBrandingCard } from '@/components/settings/HeaderBrandingCard';

type SavingField = 'name' | 'industry' | 'website' | 'address' | 'phone' | null;

export function SettingsCompany() {
  const { user, companies, loading, is_superadmin, refresh } = useSettingsContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check for company parameter in URL (from All Companies page) - uses slug for security
  const companySlugParam = searchParams.get('company');

  // Use URL company slug if provided, otherwise use user's default_company_id
  const currentCompany = companySlugParam
    ? companies.find(acc => acc.slug === companySlugParam) || companies[0]
    : (user?.default_company_id
      ? companies.find(acc => acc.id === user.default_company_id) || companies[0]
      : companies[0]);

  const [savingField, setSavingField] = useState<SavingField>(null);
  const [savedField, setSavedField] = useState<SavingField>(null);

  const [formData, setFormData] = useState({
    name: currentCompany?.name || 'Hollywood Trucks',
    slug: currentCompany?.slug || 'hollywood-trucks',
    industry: currentCompany?.industry || undefined as IndustryType | undefined,
    website: 'https://hollywoodtrucks.com',
    address: '123 Studio Blvd, Los Angeles, CA 90028',
    phone: '+1 (323) 555-0100',
    logo_url: currentCompany?.logo_url || null as string | null,
  });

  // Theme settings for custom header branding
  const [themeSettings, setThemeSettings] = useState({
    logo_light_url: currentCompany?.theme_json?.logo_light_url || null as string | null,
    logo_dark_url: currentCompany?.theme_json?.logo_dark_url || null as string | null,
    icon_light_url: currentCompany?.theme_json?.icon_light_url || null as string | null,
    icon_dark_url: currentCompany?.theme_json?.icon_dark_url || null as string | null,
    use_custom_logo: currentCompany?.theme_json?.use_custom_logo || false,
    logo_display_mode: (currentCompany?.theme_json?.logo_display_mode || 'replace') as LogoDisplayMode,
    use_single_logo: currentCompany?.theme_json?.use_single_logo || false,
    use_single_icon: currentCompany?.theme_json?.use_single_icon || false,
  });

  // Track original values to detect changes
  const originalValues = useRef<typeof formData | null>(null);

  // Update form data when currentCompany changes (from URL param or context)
  useEffect(() => {
    if (currentCompany) {
      const data = {
        name: currentCompany.name || 'Hollywood Trucks',
        slug: currentCompany.slug || 'hollywood-trucks',
        industry: currentCompany.industry || undefined as IndustryType | undefined,
        website: 'https://hollywoodtrucks.com',
        address: '123 Studio Blvd, Los Angeles, CA 90028',
        phone: '+1 (323) 555-0100',
        logo_url: currentCompany.logo_url || null,
      };
      setFormData(data);
      originalValues.current = { ...data };

      // Update theme settings from currentCompany
      setThemeSettings({
        logo_light_url: currentCompany.theme_json?.logo_light_url || null,
        logo_dark_url: currentCompany.theme_json?.logo_dark_url || null,
        icon_light_url: currentCompany.theme_json?.icon_light_url || null,
        icon_dark_url: currentCompany.theme_json?.icon_dark_url || null,
        use_custom_logo: currentCompany.theme_json?.use_custom_logo || false,
        logo_display_mode: currentCompany.theme_json?.logo_display_mode || 'replace',
        use_single_logo: currentCompany.theme_json?.use_single_logo || false,
        use_single_icon: currentCompany.theme_json?.use_single_icon || false,
      });
    }
  }, [currentCompany]);

  // Auto-save a single field
  const saveField = useCallback(async (fieldName: SavingField, value: string | undefined) => {
    if (!fieldName || !currentCompany) return;

    // Check if value actually changed
    const originalValue = originalValues.current?.[fieldName as keyof typeof formData];
    if (value === originalValue) return;

    setSavingField(fieldName);
    try {
      await updateAdminCompany(currentCompany.id, {
        name: fieldName === 'name' ? value as string : formData.name,
        slug: formData.slug,
        industry: fieldName === 'industry' ? value as IndustryType | undefined : formData.industry,
      });

      // Update original values
      if (originalValues.current) {
        (originalValues.current as Record<string, unknown>)[fieldName] = value;
      }

      // Show saved indicator briefly
      setSavedField(fieldName);
      setTimeout(() => setSavedField(null), 1500);

      // Refresh context to get updated data
      await refresh();
    } catch (error) {
      toast.error(`Failed to update ${fieldName.replace('_', ' ')}`);
      console.error('Company update error:', error);
    } finally {
      setSavingField(null);
    }
  }, [formData, currentCompany, refresh]);

  // Save theme settings (header branding)
  const saveThemeSettings = useCallback(async (updates: Partial<typeof themeSettings>) => {
    if (!currentCompany) return;

    const newTheme = { ...themeSettings, ...updates };
    setThemeSettings(newTheme);

    try {
      await updateAdminCompany(currentCompany.id, {
        theme_json: {
          logo_light_url: newTheme.logo_light_url,
          logo_dark_url: newTheme.logo_dark_url,
          icon_light_url: newTheme.icon_light_url,
          icon_dark_url: newTheme.icon_dark_url,
          use_custom_logo: newTheme.use_custom_logo,
          logo_display_mode: newTheme.logo_display_mode,
          use_single_logo: newTheme.use_single_logo,
          use_single_icon: newTheme.use_single_icon,
        }
      });
      toast.success('Branding settings updated');
      await refresh();
    } catch (error) {
      toast.error('Failed to update branding settings');
      console.error('Theme settings update error:', error);
    }
  }, [currentCompany, themeSettings, refresh]);

  // Field status indicator
  const FieldStatus = ({ field }: { field: SavingField }) => {
    if (savingField === field) {
      return <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />;
    }
    if (savedField === field) {
      return <Check className="w-4 h-4 text-[#8FB560]" />;
    }
    return null;
  };

  // Don't render until data is loaded to prevent text chunking
  if (loading) {
    return (
      <div className="space-y-8">
        <div className="animate-pulse">
          <div className="h-9 bg-muted rounded w-64 mb-2" />
          <div className="h-4 bg-muted rounded w-96" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            <div className="bg-card border border-border rounded-xl p-4 sm:p-6 h-64 animate-pulse" />
          </div>
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-xl p-4 sm:p-6 lg:p-8 h-96 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Blank state if user has no associated companies
  if (!currentCompany || companies.length === 0) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Company Profile</h1>
          <p className="text-muted-foreground mt-2">Manage your organization profile and branding</p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <Building className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Company Associated</h3>
            <p className="text-muted-foreground mb-6">
              You are not currently associated with any company. Please contact your administrator to get access to a company profile.
            </p>
            <Button className="bg-[#8FB560] hover:bg-[#7a9d51]" disabled>
              <Building className="w-4 h-4 mr-2" />
              Request Company Access
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        {companySlugParam && is_superadmin && (
          <button
            onClick={() => navigate('/settings/companies')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#8FB560] transition-colors mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Companies
          </button>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">
          {companySlugParam && currentCompany ? `${currentCompany.name}` : 'Company Profile'}
        </h1>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
          {companySlugParam ? 'Manage company profile and branding' : 'Manage your organization profile and branding'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Left Column - Logo & Branding */}
        <div className="lg:col-span-1 space-y-4 sm:space-y-6">
          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Company Logo</h3>
            <div className="flex flex-col items-center text-center">
              <ImageUploader
                currentImage={formData.logo_url}
                onUpload={async (file) => {
                  if (!currentCompany?.slug) return;
                  const result = await uploadCompanyLogo(currentCompany.slug, file);
                  const logoUrl = result.logo.svg_url || result.logo.medium_webp_url || result.logo.medium_png_url;
                  setFormData(prev => ({ ...prev, logo_url: logoUrl }));
                  toast.success('Logo uploaded successfully');
                }}
                onRemove={async () => {
                  if (!currentCompany?.slug) return;
                  await deleteCompanyLogo(currentCompany.slug);
                  setFormData(prev => ({ ...prev, logo_url: null }));
                  toast.success('Logo removed');
                }}
                accept={{
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/webp': ['.webp'],
                  'image/gif': ['.gif'],
                  'image/svg+xml': ['.svg'],
                }}
                shape="square"
                size="lg"
                imageType="logo"
                helpText="PNG, SVG, JPG, WebP or GIF. Max 5MB."
              />
            </div>
          </div>

          <HeaderBrandingCard
            themeSettings={themeSettings}
            setThemeSettings={setThemeSettings}
            saveThemeSettings={saveThemeSettings}
            companySlug={currentCompany?.slug}
            companyName={formData.name}
            refresh={refresh}
          />
        </div>

        {/* Right Column - Details Form */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <Building className="w-5 h-5 text-[#8FB560]" />
              Organization Details
            </h3>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField id="name" label="Company Name" status={<FieldStatus field="name" />}>
                  <IconInput
                    id="name"
                    icon={<Building className="w-4 h-4" />}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    onBlur={() => saveField('name', formData.name)}
                  />
                </FormField>

                <FormField id="slug" label="Slug / Identifier">
                  <Input
                    id="slug"
                    value={formData.slug}
                    disabled
                    className="bg-background border-border opacity-50"
                  />
                </FormField>

                {/* Industry dropdown - only visible to superadmins */}
                {is_superadmin && (
                  <FormField id="industry" label="Industry" status={<FieldStatus field="industry" />}>
                    <Select
                      value={formData.industry || ''}
                      onValueChange={(value) => {
                        setFormData({...formData, industry: value as IndustryType});
                        saveField('industry', value as IndustryType);
                      }}
                    >
                      <SelectTrigger id="industry" className="h-10 bg-background border-border">
                        <Briefcase className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(INDUSTRY_TYPE_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Restricts MODELS filter to this industry type
                    </p>
                  </FormField>
                )}

                <FormField id="website" label="Website" status={<FieldStatus field="website" />}>
                  <IconInput
                    id="website"
                    icon={<Globe className="w-4 h-4" />}
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    onBlur={() => saveField('website', formData.website)}
                  />
                </FormField>

                <FormField id="phone" label="Support Phone" status={<FieldStatus field="phone" />}>
                  <IconInput
                    id="phone"
                    icon={<Phone className="w-4 h-4" />}
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    onBlur={() => saveField('phone', formData.phone)}
                  />
                </FormField>
              </div>

              <FormField id="address" label="Billing Address" status={<FieldStatus field="address" />}>
                <IconInput
                  id="address"
                  icon={<MapPin className="w-4 h-4" />}
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  onBlur={() => saveField('address', formData.address)}
                />
              </FormField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
